import { useEffect, useState } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import '@fontsource/roboto/300.css';
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';
import { Route, Routes, BrowserRouter } from 'react-router-dom';
import Shell from './Components/Shell';
import { routes, publicRoutes, IRoute } from './routing';
import theme from './Theme';
import { SnackbarProvider } from 'notistack';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useAuth0 } from '@auth0/auth0-react';
import { useAppDispatch } from './store';
import { setToken } from './store';
import { LoadingIndicator, NotAuthorizedError, userHasPermissions, UserPermissionContext } from './Components/CoreLib/library';
import jwt from 'jsonwebtoken';

function App() {
    const { isLoading, isAuthenticated, error, getAccessTokenSilently } = useAuth0();
    const dispatch = useAppDispatch();
    const [accessTokenSet, setAccessTokenSet] = useState(false);
    const [userPermissions, setUserPermissions] = useState<string[] | null>(null);

    useEffect(() => {
        (async () => {
            if (isAuthenticated) {
                if (!userPermissions) {
                    const accessToken = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });
                    const decodedToken = jwt.decode(accessToken);
                    const permissions = ((decodedToken as any)?.permissions as string[]) ?? [];
                    
                    setUserPermissions(permissions);
                    dispatch(setToken(accessToken));
                    setAccessTokenSet(true);
                }
            }
        })()
    }, [userPermissions, setUserPermissions, isAuthenticated, getAccessTokenSilently, dispatch]);

    const togglePermission = (permission: string ): void => {
        if (!userPermissions) {
            return;
        }
        const isPermissionActive = userPermissions.includes(permission);
        if (isPermissionActive) {
            setUserPermissions([...userPermissions.filter(p => p !== permission)]);
        } else {
            setUserPermissions([...userPermissions, permission]);
        }
    }

    const renderRoute = (route: IRoute) => {
        if (userPermissions && userHasPermissions(route.requiredPermissions ?? [], userPermissions)) {
            return <Route key={route.path} path={route.path} element={route.component} />;
        } else {
            return <Route key={route.path} path={route.path} element={<NotAuthorizedError />} />;
        }
    };

    const renderPublicRoute = (route: IRoute) => {
        return <Route key={route.path} path={route.path} element={route.component} />;
    };

    if (isLoading || (isAuthenticated && !accessTokenSet)) {
        return <LoadingIndicator />;
    }

    if (error) {
        return <p>{error.message}</p>;
    }

    return (
        <BrowserRouter>
            <ThemeProvider theme={theme}>
                <CssBaseline>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <SnackbarProvider maxSnack={3}>
                            <UserPermissionContext.Provider value={{
                                permissions: userPermissions ?? [],
                                togglePermission: togglePermission
                            }}>
                                <Routes>
                                    <Route path='/' element={<Shell isProtected={true} />}>
                                        {routes.map(renderRoute)}
                                    </Route>
                                    <Route path='/' element={<Shell />}>
                                        {publicRoutes.map(renderPublicRoute)}
                                    </Route>
                                </Routes>
                            </UserPermissionContext.Provider>
                        </SnackbarProvider>
                    </LocalizationProvider>
                </CssBaseline>
            </ThemeProvider>
        </BrowserRouter>
    );
}

export default App;
